<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center gap-4">
          <v-btn icon @click="$router.push('/supplies-manager')" class="bg-white border border-slate-200 shadow-sm rounded-lg">
            <v-icon color="slate-600">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-2xl font-black text-slate-800 tracking-tight">
              Nova Autorização de Abastecimento
            </h1>
            <p class="text-sm text-slate-500 font-medium mt-1">
              {{ $route.name }} - Selecione o fornecedor e o veículo
            </p>
          </div>
        </div>
      </div>

      <!-- MAIN CARD -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <!-- Divisor Escuro -->
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-gas-station</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            SELEÇÃO DE FORNECEDOR E VEÍCULO
          </h2>
        </div>

        <div class="p-6">
          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="space-y-6">
              <!-- Provider Selection -->
              <div class="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <v-autocomplete
                  label="Fornecedor:"
                  :rules="fieldRules"
                  :items="providers"
                  item-value="provider_id"
                  item-text="provider_name"
                  v-model="data.provider_id"
                  outlined
                  dense
                  class="bg-white rounded-lg"
                  hide-details
                />
              </div>
              
              <!-- Vehicle Table -->
              <div class="border border-slate-200 rounded-xl overflow-hidden mt-6">
                <v-data-table
                  :headers="headers"
                  :items="vehicles"
                  :items-per-page="10"
                  :loading="loader"
                  loading-text="CARREGANDO..."
                  class="elevation-0"
                  no-data-text="Nenhum veículo encontrado"
                >
                  <template v-slot:item.model_desc="{ item }">
                    <div class="flex flex-col py-2" v-if="item.vehicle_id">
                      <span class="text-sm font-bold text-slate-800">{{ item.vehicle_board }}</span>
                      <span class="text-[10px] text-slate-500 font-medium uppercase">{{ item.brand_desc }} - {{ item.model_desc }}</span>
                    </div>
                    <span v-else class="text-slate-400 italic">Veículo não definido</span>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <div class="flex items-center justify-end gap-2">
                      <v-btn
                        :disabled="data.provider_id === null"
                        small
                        depressed
                        color="primary"
                        :loading="loader"
                        @click="addFuelAuthorization(item)"
                        class="rounded-lg font-bold"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        Criar Abastecimento
                      </v-btn>
                      <v-btn
                        small
                        outlined
                        color="slate-600"
                        :loading="loader"
                        @click="loadAbastecimento(item)"
                        class="rounded-lg font-bold border-slate-300"
                      >
                        <v-icon left x-small>mdi-history</v-icon>
                        Histórico
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </div>

    <!-- MODAL HISTÓRICO -->
    <v-dialog v-model="showModalAbastecimento" max-width="1200px" scrollable>
      <v-card class="rounded-xl overflow-hidden">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-history</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              {{ modalTitleAbastecimento }}
            </h2>
          </div>
          <v-btn icon dark small @click="showModalAbastecimento = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-0">
          <v-data-table
            :headers="headersAbastecimento"
            :items="abastecimentoData"
            :loading="loaderAbastecimento"
            loading-text="CARREGANDO..."
            class="elevation-0"
            no-data-text="Nenhuma autorização de abastecimento encontrada"
          >
            <template v-slot:item.datehour="{ item }">
              <span class="text-sm text-slate-600 font-medium">{{ convertDateTime(item.datehour) }}</span>
            </template>
            <template v-slot:item.supply_authorized_value="{ item }">
              <span class="text-sm font-bold text-slate-800">{{ realMask(item.supply_authorized_value.toFixed(2)) }}</span>
            </template>
            <template v-slot:item.supply_authorized_liters="{ item }">
              <span class="text-sm font-bold text-emerald-600">{{ realMaskII(item.supply_authorized_liters.toFixed(3)) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- MODAL NOVA AUTORIZAÇÃO -->
    <v-dialog v-model="showModal" max-width="600px" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-plus-circle</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              {{ modalTitle }}
            </h2>
          </div>
          <v-btn icon dark small @click="showModal = false; clearForm()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-6">
          <v-form ref="formMotorista" v-if="!loadingModal" v-model="validForm" lazy-validation>
            <div class="space-y-6">
              <div class="bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex items-center gap-3 mb-4">
                <v-icon color="indigo">mdi-information-outline</v-icon>
                <div class="text-[11px] text-indigo-700 font-medium leading-tight">
                  Defina os detalhes da autorização extra para este veículo.
                </div>
              </div>

              <v-autocomplete
                label="Item / Combustível:"
                :rules="fieldRules"
                :items="items"
                item-value="item_id"
                :item-text="itemLabel"
                v-model="data.item_id"
                outlined
                dense
                class="bg-white rounded-lg"
                no-data-text="Nenhum item encontrado"
              >
                <template slot="selection" slot-scope="{ item }">
                  <span class="text-sm font-bold text-slate-700">{{ itemLabel(item) }}</span>
                  <span class="text-[10px] text-slate-500 ml-2">(R$: {{ realMask(Number(item.item_price || 0).toFixed(2)) }})</span>
                </template>
                <template slot="item" slot-scope="{ item }">
                  <div class="flex items-center justify-between w-full">
                    <span class="text-sm font-medium">{{ itemLabel(item) }}</span>
                    <span class="text-[10px] font-bold text-emerald-600">R$: {{ realMask(Number(item.item_price || 0).toFixed(2)) }}</span>
                  </div>
                </template>
              </v-autocomplete>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Litros Autorizados"
                    v-model="data.supply_authorized_liters"
                    v-mask-decimal.br="3"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                    :disabled="disableLitros"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Valor Autorizado (R$)"
                    v-model="data.supply_authorized_value"
                    v-mask-decimal.br="2"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                    :disabled="disableValor"
                  />
                </v-col>
              </v-row>
            </div>

            <div class="flex items-center justify-end gap-3 mt-8 border-t border-slate-100 pt-6">
              <v-btn 
                depressed
                color="secondary"
                text
                @click="[showModal = false, clearForm()]"
                class="rounded-lg font-bold"
              >
                Cancelar
              </v-btn>
              <v-btn
                depressed
                color="primary"
                :loading="loadingSubmit"
                :disabled="!validForm"
                @click="createSupplyAuthorization(data)"
                class="rounded-lg font-bold px-8 shadow-sm"
              >
                Confirmar Autorização
              </v-btn>
            </div>
          </v-form>
          <div v-else class="flex flex-col items-center justify-center py-12">
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
              width="4"
            ></v-progress-circular>
            <span class="mt-4 text-sm font-medium text-slate-500">Carregando detalhes...</span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- MODAL SENHA -->
    <v-dialog v-model="showPassword" width="350" persistent>
      <v-card class="rounded-2xl overflow-hidden shadow-2xl border-2 border-indigo-100">
        <div class="bg-indigo-600 p-6 text-center">
          <v-icon color="white" large class="mb-2">mdi-lock-check</v-icon>
          <h2 class="text-white font-black text-xl tracking-tight leading-tight uppercase">
            Sua senha foi gerada!
          </h2>
        </div>
        
        <v-card-text class="p-8 text-center bg-white">
          <div class="space-y-6">
            <div class="bg-slate-50 p-6 rounded-2xl border-2 border-dashed border-slate-200">
              <input
                readonly
                class="text-4xl font-black text-indigo-700 text-center tracking-widest w-full bg-transparent border-0 outline-none"
                ref="copiar"
                :value="password"
              />
            </div>
            
            <v-btn
              :color="copiado ? 'emerald' : 'indigo'"
              depressed
              dark
              block
              large
              @click="copy"
              class="rounded-xl font-black text-sm tracking-widest h-14"
            >
              <v-icon left size="20">{{ copiado ? 'mdi-check-circle' : 'mdi-content-copy' }}</v-icon>
              {{ copiado ? "COPIADO COM SUCESSO" : "COPIAR SENHA" }}
            </v-btn>
            
            <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              Apresente esta senha ao frentista no momento do abastecimento.
            </p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- NOTIFICAÇÃO -->
    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      {{snack.message}}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    valid: false,
    loader: false,
    showForm: false,
    showPassword: false,
    copiado: false,
    password: "",
    providers: [],
    vehicles: [],
    items: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_authorized_liters: "0",
      supply_authorized_value: "0",
      supply_status: "PRE-AUTORIZADO",
      supply_adjusted: false,
      supply_extra: true,
      supply_liters_stocked: false,
      supply_billed: false,
      vehicle_id: null,
      driver_id: null,
      item_id: null,
      prefecture_id: null,
      provider_id: null,
    },
    headers: [
      { text: "Veículo", value: "model_desc" },
      { text: "Ações", align: "right", value: "action", sortable: false },
    ],
    headersAbastecimento: [
      { text: "Data e Hora", value: "datehour", align: "center" },
      { text: "Fornecedor", value: "provider_name" },
      { text: "Item", value: "item_description_desc" },
      { text: "Litros Autorizados", value: "supply_authorized_liters", align: "center" },
      { text: "Valor Autorizado", value: "supply_authorized_value", align: "center" },
      { text: "Status", value: "supply_status", align: "center" }
    ],
    loaderAbastecimento: false,
    abastecimentoData: [],
    showModalAbastecimento: false,
    modalTitleAbastecimento: "Abastecimentos",
    modalTitle: "Novo Abastecimento",
    showModal: false,
    loadingModal: false,
    loadingSubmit: false,
    validForm: false,
    vehicle_tank_capacity: 0
  }),
  created() {
    this.Helpers;
    this.$http
      .get(`/providers/prefecture/${this.$store.getters.getUserData.table_id}`)
      .then((r) => (this.providers = r.data));
    this.$http
      .get(`/vehicles/supplies/${this.$store.getters.getUserData.table_id}`)
      .then((r) => (this.vehicles = r.data.filter(v => (v.vehicle_active === 1 || v.vehicle_active === true) && (v.abastecimento_extra === 1 || v.abastecimento_extra === true))));
    this.data.prefecture_id = this.$store.getters.getUserData.table_id;
  },
  computed: {
    disableLitros() {
      return parseFloat(this.data.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.data.supply_authorized_liters.replace(',', '.')) > 0;
    }
  },
  methods: {
    itemLabel(item) {
      if (!item) return "";
      return item.item_description_desc || item.item_name || "";
    },
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
            this.$router.push("/supplies-manager");
          }, 1000);
        } else {
          alert("não copiado!");
        }
      } catch (err) {
        alert(
          "Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C."
        );
      }
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
      this.items = res.data;
      this.data.item_id = null;
    },
    async loadAbastecimento(vehicle) {
      this.loaderAbastecimento = true;
      this.abastecimentoData = [];
      this.modalTitleAbastecimento = `Abastecimentos - Veículo: ${vehicle.vehicle_board}`;
      this.showModalAbastecimento = true;

      const filter = {
        prefecture_id: vehicle.prefecture_id,
        vehicle_id: vehicle.vehicle_id,
        all_status: true,
        supply_extra: true,
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date().toISOString().split("T")[0],
      }

      const res = await this.$http.put("/supplies/billings", filter);
      this.abastecimentoData = res.data;

      this.loaderAbastecimento = false;
    },
    clearForm() {
      this.data = {
        supply_authorized_liters: "0",
        supply_authorized_value: "0",
        supply_status: "PRE-AUTORIZADO",
        supply_adjusted: false,
        supply_extra: true,
        supply_liters_stocked: false,
        supply_billed: false,
        vehicle_id: null,
        driver_id: null,
        item_id: null,
        prefecture_id: this.data.prefecture_id,
        provider_id: this.data.provider_id,
      };
      this.items = [];
    },
    async addFuelAuthorization(item) {
      this.clearForm();
      this.modalTitle = `Novo Abastecimento - Veículo: ${item.vehicle_board}`;
      this.showModal = true;
      this.loadingModal = true;

      this.vehicle_tank_capacity = item.vehicle_tank_capacity;

      this.data.vehicle_id = item.vehicle_id;
      await this.loadItems(this.data.provider_id, this.data.vehicle_id, this.data.prefecture_id).catch(() => {});

      this.loadingModal = false;
    },
    async createSupplyAuthorization(data) {
      if (this.$refs.formMotorista.validate()) {
        const supply_authorized_liters = parseFloat(data.supply_authorized_liters.replace(',', '.'));

        if (this.vehicle_tank_capacity > 0 && supply_authorized_liters > this.vehicle_tank_capacity) {
          this.snack.show = true;
          this.snack.color = "warning";
          this.snack.message = "Litros autorizados acima da capacidade máxima do veículo!"
          this.loadingSubmit = false;
          return;
        }

        try {
          this.loadingSubmit = true;
          data.is_autorized = true;
          data.authorizer = this.$store.getters.getUserData.user_login;
          await this.$http.post("/supplies/single", data);
          this.snack.show = true;
          this.snack.message = "Autorização de abastecimento criada com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
  },
};
</script>
